/**
 * One place that knows how to write a CSV file correctly.
 *
 * WHY THIS EXISTS. Board #459: every export in this app built its rows with
 * `cells.join(',')`. That is not CSV — it is a string with commas in it. A description
 * containing a comma silently shifts every later column on that row; one containing a quote or
 * a newline corrupts the file from that point on. The user finds out in their spreadsheet,
 * long after the export, with no indication anything went wrong.
 *
 * Eight files carry their own copy of that pattern (Expenses, Customers, Employees, Invoices,
 * Jobs, JobsHistory, AgingReport, ModernTable). This module is the owner; #459 repoints
 * Expenses, and the rest are a tracked follow-up rather than a silent sweep.
 *
 * TWO THINGS BEYOND QUOTING, both of which matter:
 *
 * 1. FORMULA INJECTION. Excel, Google Sheets and LibreOffice all EXECUTE a cell beginning
 *    `=`, `+`, `-`, `@`, tab or CR. A vendor name of `=HYPERLINK("http://evil","Click")`, or
 *    `=cmd|'/c calc'!A1` in older Excel, runs when the accountant opens the export. The data
 *    here is user-entered and is exported by a different person than entered it, which is
 *    exactly the threat model. Such cells are prefixed with a single quote, the conventional
 *    neutraliser: the text still reads correctly, it just is not a formula.
 * 2. THE BOM. Excel reads a UTF-8 file without a byte-order mark as the local codepage, so
 *    accented names and the £/€ signs arrive mangled. Three bytes fixes it and nothing else
 *    reads them as content.
 */

/** Quote per RFC 4180: wrap when needed, and double any embedded quote. */
const escapeCell = (value) => {
  if (value === null || value === undefined) return '';

  let text = String(value);

  // Neutralise a leading formula character BEFORE quoting, so the guard is inside the quotes
  // and survives the round trip.
  // A plain number (-29.96, +5) is not a formula — prefixing it turned every negative money
  // value into text in Excel (board #4357).
  if (/^[=+\-@\t\r]/.test(text) && !/^[-+]?\d+(\.\d+)?$/.test(text)) {
    text = `'${text}`;
  }

  // A comma, quote, newline or carriage return all require the field to be quoted. Leading or
  // trailing spaces are quoted too, since some parsers strip them otherwise.
  if (/[",\n\r]/.test(text) || /^\s|\s$/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/**
 * Build a CSV document from a header array and an array of row arrays.
 *
 * CRLF line endings, because RFC 4180 specifies them and Excel on Windows is the single most
 * common destination for these files.
 */
export const toCsv = (headers, rows) => {
  const lines = [headers.map(escapeCell).join(',')];
  rows.forEach((row) => lines.push(row.map(escapeCell).join(',')));
  return lines.join('\r\n');
};

/**
 * Download a CSV in the browser, with the BOM Excel needs.
 *
 * The object URL is revoked after the click so repeated exports do not leak blobs for the life
 * of the tab.
 */
export const downloadCsv = (filename, headers, rows) => {
  const csv = toCsv(headers, rows);
  // ﻿ — see the BOM note above.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Read a CSV document into an array of row arrays. Minimal RFC-4180: quoted fields, embedded
 * commas and newlines, doubled quotes, CRLF, and the BOM downloadCsv writes.
 *
 * Moved here from EmployeeImportModal (board #459) so the importers can read what the
 * exporters above write. `split(',')` cannot: every export quotes a field containing a comma.
 */
export const parseCsv = (text) => {
  const source = String(text || '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (inQuotes) {
      if (ch === '"') {
        if (source[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { row.push(field); field = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
    field += ch;
  }
  row.push(field);
  rows.push(row);

  // Drop trailing blank lines — a CSV almost always ends with a newline.
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
};

/**
 * Undo escapeCell's formula guard: `'=SUM(...)` or `'-5` back to what was exported. Only a
 * quote followed by a guarded character is removed, so a value that genuinely starts with an
 * apostrophe ("'90s kitchen") is left alone.
 */
export const unguardCell = (value) => (
  /^'[=+\-@\t\r]/.test(String(value ?? '')) ? String(value).slice(1) : value
);

export default { toCsv, downloadCsv, parseCsv, unguardCell };
